'use client';
import React, { forwardRef } from 'react';

interface TextAreaProps {
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  label?: string;
  error?: string;
  id?: string;
  name?: string;
  rows?: number;
  cols?: number;
  maxLength?: number;
  minLength?: number;
  readOnly?: boolean;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'outlined';
  autoComplete?: string;
  autoFocus?: boolean;
  style?: React.CSSProperties;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      placeholder,
      value,
      onChange,
      disabled = false,
      required = false,
      className = '',
      label,
      error,
      id,
      name,
      rows = 4,
      cols,
      maxLength,
      minLength,
      readOnly = false,
      resize = 'vertical',
      size = 'md',
      variant = 'default',
      autoComplete,
      autoFocus = false,
      style,
      ...props
    },
    ref
  ) => {
    const sizes = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
    };

    const variants = {
      default: 'border border-text-muted bg-secondary focus:border-text-secondary-main',
      filled: 'border-0 bg-secondary-light focus:border-text-secondary-main',
      outlined: 'border border-text-muted bg-transparent focus:border-text-secondary-main',
    };

    const resizeClasses = {
      none: 'resize-none',
      both: 'resize',
      horizontal: 'resize-x',
      vertical: 'resize-y',
    };

    const baseClasses = `
    w-full
    rounded-lg
    font-inter
    transition-all
    duration-200
    ease-in-out
    focus:outline-none
    focus:ring-0
    focus:border-text-secondary-main
    placeholder:text-text-muted
    ${value ? 'border-text-secondary-main' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed bg-bg-light' : ''}
    ${error ? 'border-red-500 focus:border-red-500' : ''}
    ${readOnly ? 'bg-bg-light cursor-default' : ''}
    ${resizeClasses[resize]}
  `
      .trim()
      .replace(/\s+/g, ' ');

    return (
      <div data-component-id="src/components/ui/TextArea.tsx:96:6" data-component-path="src/components/ui/TextArea.tsx" data-component-line="96" data-component-end-line="141" data-component-file="TextArea.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%22%7D" className="w-full">
        {label && (
          <label data-component-id="src/components/ui/TextArea.tsx:98:10" data-component-path="src/components/ui/TextArea.tsx" data-component-line="98" data-component-end-line="101" data-component-file="TextArea.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22block%20text-sm%20font-medium%20text-text-primary%20mb-2%22%7D" htmlFor={id} className="block text-sm font-medium text-text-primary mb-2">
            {label}
            {required && <span data-component-id="src/components/ui/TextArea.tsx:100:25" data-component-path="src/components/ui/TextArea.tsx" data-component-line="100" data-component-end-line="100" data-component-file="TextArea.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-red-500%20ml-1%22%2C%22textContent%22%3A%22*%22%7D" className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <textarea data-component-id="src/components/ui/TextArea.tsx:103:8" data-component-path="src/components/ui/TextArea.tsx" data-component-line="103" data-component-end-line="128" data-component-file="TextArea.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22id%22%3A%22%5Bvar%3Aid%5D%22%2C%22name%22%3A%22%5Bvar%3Aname%5D%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
          ref={ref}
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          rows={rows}
          cols={cols}
          maxLength={maxLength}
          minLength={minLength}
          readOnly={readOnly}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={`
          ${baseClasses}
          ${sizes[size]}
          ${variants[variant]}
          ${className}
        `
            .trim()
            .replace(/\s+/g, ' ')}
          {...props}
        />
        <div data-component-id="src/components/ui/TextArea.tsx:129:8" data-component-path="src/components/ui/TextArea.tsx" data-component-line="129" data-component-end-line="140" data-component-file="TextArea.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-center%20mt-1%22%7D" className="flex justify-between items-center mt-1">
          {error && (
            <p data-component-id="src/components/ui/TextArea.tsx:131:12" data-component-path="src/components/ui/TextArea.tsx" data-component-line="131" data-component-end-line="133" data-component-file="TextArea.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-red-500%22%7D" className="text-sm text-red-500" role="alert">
              {error}
            </p>
          )}
          {maxLength && (
            <p data-component-id="src/components/ui/TextArea.tsx:136:12" data-component-path="src/components/ui/TextArea.tsx" data-component-line="136" data-component-end-line="138" data-component-file="TextArea.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-muted%20ml-auto%20py-4%20px-4%20max-h-%5B150px%5D%22%2C%22textContent%22%3A%22%2F%22%7D" className="text-sm text-text-muted ml-auto py-4 px-4 max-h-[150px]">
              {value?.length || 0}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
